import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useT, type T } from "@/i18n";
import { Toast, useToast } from "@/components/Toast";
import { fetchComposition, saveComposition, type Composition } from "@/services/productProfiles";

// His "Composition du meuble": how many parts this product is stored in (Hudson = canapé +
// méridienne). Each part gets its own label. Set once per product, used by every later intake.

// His `defaultPieceNames`: a sofa in two parts is "Sofa" + "Chaise longue", anything else "Piece i".
export const defaultParts = (t: T, n: number, sofa: boolean) =>
  sofa && n === 2 ? [t("compo.sofa"), t("compo.chaise")] : Array.from({ length: n }, (_, i) => `${t("stock.part")} ${i + 1}`);

// The parts as saved, or his suggestion (a sofa that says it is L-shaped) until someone saves one.
export const partsOf = (t: T, c: Composition | undefined) =>
  c?.piece_names.length ? c.piece_names : c?.suggested ? defaultParts(t, 2, true) : [];

// His compo line on the chosen piece: "Composition: 2 pieces (Sofa · Chaise longue) [to confirm]" + Edit.
export default function CompositionEditor({ productId, productName }: { productId: number; productName?: string | null }) {
  const { t } = useT();
  const current = useQuery({ queryKey: ["composition", productId], queryFn: () => fetchComposition(productId) });
  const [open, setOpen] = useState(false);
  const parts = partsOf(t, current.data);
  return (
    <p className="mt-1 text-xs text-slate-500">
      {t("compo.title")}: <b className="text-slate-700">{parts.length > 1 ? t("stock.nParts", { n: parts.length }) : t("compo.single")}</b>
      {parts.length > 1 && <span> ({parts.join(" · ")})</span>}
      {current.data?.suggested && <span className="ml-1 rounded bg-amber-100 px-1 text-amber-900">{t("compo.toConfirm")}</span>}
      <button onClick={() => setOpen(true)} className="ml-2 underline">{t("compo.edit")}</button>
      {open && <CompositionDialog productId={productId} productName={productName} onClose={() => setOpen(false)} />}
    </p>
  );
}

// His "n pieces" badge as a button that opens the dialog — "?" while it is only a suggestion. A span,
// because it sits inside the grid's own button.
export function CompositionBadge({ productId, productName, parts, suggested }: {
  productId: number; productName?: string | null; parts: number; suggested?: boolean;
}) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  if (parts < 2) return null;
  return (
    <>
      <span role="button" tabIndex={0} title={t("compo.dialogTitle")}
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen(true); }}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); setOpen(true); } }}
        className={`ml-1 cursor-pointer rounded px-1 text-[11px] font-semibold ${suggested ? "bg-amber-50 text-amber-800 ring-1 ring-dashed ring-amber-400" : "bg-amber-100 text-amber-900"}`}>
        {t("stock.nParts", { n: parts })}{suggested ? " ?" : ""}
      </span>
      {open && <CompositionDialog productId={productId} productName={productName} onClose={() => setOpen(false)} />}
    </>
  );
}

// His `openPieces` dialog: − / + from 1 to 8, the name of each part ("Piece i/n"), Save or Cancel.
export function CompositionDialog({ productId, productName, onClose }: { productId: number; productName?: string | null; onClose: () => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [toast, showToast] = useToast();
  const current = useQuery({ queryKey: ["composition", productId], queryFn: () => fetchComposition(productId) });
  const start = partsOf(t, current.data);
  const [n, setN] = useState<number | null>(null);
  const [names, setNames] = useState<string[] | null>(null);
  const count = n ?? Math.max(1, start.length);
  // His `renderPcs`: names kept as typed; any new slot gets the default for its position.
  const sofa = !!current.data?.suggested || (start.length === 2 && start[0] === t("compo.sofa"));
  const defaults = defaultParts(t, Math.max(count, 2), sofa);
  const shown = Array.from({ length: count }, (_, i) => (names ?? start)[i] ?? defaults[i] ?? `${t("stock.part")} ${i + 1}`);
  const save = useMutation({
    mutationFn: () => saveComposition(productId, count > 1 ? shown.map((s) => s.trim() || `${t("stock.part")}`) : []),
    onSuccess: (c) => {
      qc.setQueryData(["composition", productId], { ...c, suggested: false });
      void qc.invalidateQueries({ queryKey: ["stock-return"] });
      showToast(t("compo.saved"));
      window.setTimeout(onClose, 900);
    },
  });

  return (
    <div role="dialog" aria-modal="true" onClick={(e) => { e.stopPropagation(); onClose(); }}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-scrim/40 p-4">
      <div onClick={(e) => e.stopPropagation()} className="mt-16 w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold text-slate-900">{t("compo.dialogTitle")}</h2>
            <p className="text-sm text-slate-500">{productName ?? ""} · <span className="font-mono">{productId}</span></p>
          </div>
          <button onClick={onClose} aria-label="✕" className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
        <p className="mt-3 text-xs text-slate-600">{t("compo.dialogHint")}</p>
        <div className="mt-3 flex items-center gap-3" role="group" aria-label={t("compo.dialogTitle")}>
          <button onClick={() => { setNames(shown); setN(Math.max(1, count - 1)); }} disabled={count <= 1}
            className="h-9 w-9 rounded-lg ring-1 ring-slate-300 disabled:opacity-40" aria-label="−">−</button>
          <b className="font-mono text-lg tabular-nums">{count}</b>
          <span className="text-sm text-slate-600">{count > 1 ? t("stock.nParts", { n: count }).replace(/^\d+\s*/, "") : t("compo.single").replace(/^\d+\s*/, "")}</span>
          <button onClick={() => { setNames(shown); setN(Math.min(8, count + 1)); }} disabled={count >= 8}
            className="h-9 w-9 rounded-lg ring-1 ring-slate-300 disabled:opacity-40" aria-label="+">+</button>
        </div>
        {count > 1 && (
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {shown.map((name, i) => (
              <label key={i} className="text-xs text-slate-500">{t("compo.pieceOf", { i: i + 1, n: count })}
                <input value={name} onChange={(e) => setNames(shown.map((x, j) => (j === i ? e.target.value : x)))}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-900" />
              </label>
            ))}
          </div>
        )}
        <div className="mt-4 flex gap-2">
          <button onClick={() => save.mutate()} disabled={save.isPending} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">{t("compo.save")}</button>
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm ring-1 ring-slate-300">{t("compo.cancel")}</button>
        </div>
        {save.error && <p className="mt-2 text-sm text-red-700">{(save.error as Error).message}</p>}
      </div>
      <Toast message={toast} />
    </div>
  );
}
